import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES } from "@/lib/i18n";
import { switchLocaleUrl } from "@/lib/localeRoutes";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Check, ChevronDown } from "lucide-react";

/**
 * Language selector. Mirrors the CurrencySelector visual treatment so they sit
 * cleanly side-by-side in the Header. Switching language triggers a full
 * navigation to the locale-prefixed URL (Iter 67dz) so the page reloads with
 * the right `<html lang>` + hreflang head tags for SEO.
 */
export default function LanguageSelector() {
  const { i18n, t } = useTranslation();
  const current = SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language) || SUPPORTED_LANGUAGES[0];

  const handleSwitch = (code) => {
    if (code === current.code) return;
    // Full reload — basename for BrowserRouter is baked at boot so we
    // can't update it imperatively. The hard nav is cheap (cached
    // bundle) and gives us a clean head-tag rewrite for free.
    window.location.assign(switchLocaleUrl(code));
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="inline-flex items-center gap-1.5 rounded-full border border-border bg-white px-3 py-1.5 text-xs font-medium hover:bg-neutral-50 transition"
        data-testid="language-selector"
        aria-label={t("header.languageLabel")}
      >
        <span className="text-sm leading-none">{current.flag}</span>
        <span className="tracking-wide">{current.label}</span>
        <ChevronDown className="h-3 w-3 opacity-60" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[180px]">
        {SUPPORTED_LANGUAGES.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => handleSwitch(lang.code)}
            className="cursor-pointer"
            data-testid={`language-option-${lang.code}`}
          >
            <span className="mr-2 text-base leading-none">{lang.flag}</span>
            <span className="flex-1 text-sm">{lang.name}</span>
            {lang.beta && (
              <span
                className="mr-2 text-[9px] uppercase tracking-wider font-bold text-amber-700 bg-amber-100 border border-amber-200 rounded-full px-1.5 py-px"
                data-testid={`language-beta-badge-${lang.code}`}
              >
                Beta
              </span>
            )}
            <span className="text-[10px] tracking-wider text-neutral-500 mr-2">{lang.label}</span>
            {i18n.language === lang.code && <Check className="h-3.5 w-3.5 text-blue-600" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
